import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GetClassroomByTeacher } from '@edtech-ui/core/models/Classroom/get-classroomByTeacher';
import { QuizItem } from '@edtech-ui/core/models/quiz-review-interface.ts/get-all-quiz';
import { ClassroomManagementService } from '@edtech-ui/core/services/classroom-management-service/classroom-management.service';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { PaginatorModule } from 'primeng/paginator';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-quizzes',
  standalone: true,
  imports: [PaginatorModule, CommonModule, FormsModule],
  templateUrl: './quizzes.component.html',
  styleUrl: './quizzes.component.scss'
})
export class QuizzesComponent {
 
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private classroomService: ClassroomManagementService,
    private quizServive: QuizManagementService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) {}

  classrooms: GetClassroomByTeacher[] = [];
  searchText = '';
  selectedClassroom: number | 'all' = 'all';
  selectedStatus: string | 'all' = 'all';
  page = 1;
  limit = 10;
  totalRecords = 0;
  quizzes: QuizItem[] = [];
  classroomId!: number;
  quizId!: string;
  quizData: any = null;
  isLoadingDetails = false;
  isLoadingList = false;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const clsId = params.get("classroomId");
        const qId = params.get("quizId");
        this.classroomId = clsId ? Number(clsId) : 0;
        this.quizId = qId || '';
        if (this.quizId) this.loadQuizDetails();
      });

    this.loadClassrooms();

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.page = 1;
        this.loadQuizzes();
      });

    this.loadQuizzes();
  }

  loadQuizDetails() {
    if (!this.quizId) return;

    this.isLoadingDetails = true;

    this.quizServive.getQuizById(this.quizId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoadingDetails = false;

          if (res.Success && res.Data) {
            this.quizData = res.Data;
          } else {
            this.toastr.error(res.Message || "Failed to retrieve quiz details");
          }
        },
        error: () => {
          this.isLoadingDetails = false;
          this.toastr.error("Error fetching quiz details");
        }
      });
  }

  searchQuiz(value: string) {
    this.searchSubject.next(value);
  }

  clearClassroom() {
    this.selectedClassroom = 'all';
    this.applyFilter();
  }

  clearStatus() {
    this.selectedStatus = 'all';
    this.applyFilter();
  }

  applyFilter() {
    this.page = 1;
    this.loadQuizzes();
  }

  onPageChange(event: any) {
    this.page = event.page + 1;
    this.limit = event.rows;
    this.loadQuizzes();
  }

  loadClassrooms() {
    this.classroomService.getAllTeacherClassrooms('all', 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.Success && res.Data?.classrooms) {
            this.classrooms = res.Data.classrooms;
          }
        },
        error: () => this.toastr.error("Failed to load classrooms")
      });
  }

  loadQuizzes() {
    this.isLoadingList = true;

    this.quizServive.getQuizzes({
      page: this.page,
      page_size: this.limit,
      search: this.searchText || null,
      status_filter: this.selectedStatus === "all" ? null : this.selectedStatus,
      classroom_filter:
        this.selectedClassroom === "all" ? null : Number(this.selectedClassroom)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoadingList = false;

          if (res.Success) {
            this.quizzes = res.Data.quizzes;
            this.totalRecords = res.Data.total_count;

            if (this.quizzes.length === 0) {
              this.toastr.info("No quizzes found");
            }
          }
        },
        error: () => {
          this.isLoadingList = false;
          this.toastr.error("Failed to load quizzes");
        }
      });
  }

  getSelectedClassroomName(): string {
    const classroom = this.classrooms.find(c => c.id === this.selectedClassroom);
    return classroom ? classroom.name : '';
  }

  getClassroomIdByName(name: string): number | null {
    const cls = this.classrooms.find(c => c.name === name);
    return cls ? cls.id : null;
  }

  goToQuizPage(q: any) {
    const quizId = q.quiz_id;
    const classroomName = q.classroom;

    const classroomId = this.getClassroomIdByName(classroomName);

    if (!quizId || !classroomId) {
      this.toastr.error("Unable to navigate: Missing classroom or quiz ID");
      return;
    }

    this.router.navigate([
      'teacher-dashboard/quiz-management',
      classroomId,
      quizId
    ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
